import { ng } from "../ngds";

// Depends on ngds alone (no protoGen/funlib, which pull in .tsx), so a Node service such as the
// sx-center print module can import it as it is.

// Self-weight and mass of a beam element, from one source of truth.
//
// The self-weight line load an app applies to the analysis and the element weight it prints must be
// the SAME number, or the printout discredits the analysis. So there is one calculation here: the
// line load, piecewise linear along the element. The mass is only ever derived from it (∫q dx / g),
// never computed separately.
//
// Quantities follow the carbon take-off in WS_PRE_Stress (carbon_footprint.cpp): bar length from the
// standalone bar end points, link count floor(length / s) + 1, and link length from
// LinkCentreLineLength (closed rectangle on the section bounding box inset by cover + dia/2, plus two
// hooks of max(10 dia, 70 mm)). Keep the two in step. Unlike the carbon volume, which is gross on
// purpose, the concrete here is NET of the steel it displaces: this is a mass, and every kilogram
// is counted once.

/** Gravitational acceleration [m/s²] used to turn mass into self-weight and back. */
export const GRAVITY = 9.81;

/** Density used when a reinforcement material carries none [kg/m³] (as the carbon take-off). */
const DEFAULT_STEEL_DENSITY = 7850;

/**
 * The databases a calculation looks materials and sections up in. Only these two are read, so a
 * caller holding the project as JSON can pass it as it is instead of converting the whole input.
 */
export type ElementDbs = Pick<ng.input.IData, "mtrlDb" | "secDb">;

/** One trapezoidal piece of the self-weight line load: positions along the element [m], values [N/m]. */
export interface SelfWeightPiece {
  start: number;
  end: number;
  qStart: number;
  qEnd: number;
}

/**
 * The density [kg/m³] an element of this material weighs with.
 *
 * - concrete: `conData.properties.density` — plain concrete; the reinforcement is added separately.
 * - steel: `steelData.properties.density`.
 * - timber: the MEAN density. `solidData.density` is ρ_k, a 5 % fractile, which understates the
 *   weight by some 15-20 % (C24: 350 against 420). Board products carry no mean, so ρ_k it is.
 * - reinforcement: `reinforcementData.properties.density`, 7850 when missing.
 */
export function elementDensity(mtrl: ng.material.Data | null | undefined): number {
  if (!mtrl) return 0;
  switch (mtrl.type) {
    case ng.material.Mtrl.MTRL_CONCRETE:
      return mtrl.getConData()?.properties?.density || 0;
    case ng.material.Mtrl.MTRL_STEEL:
      return mtrl.getSteelData()?.properties?.density || 0;
    case ng.material.Mtrl.MTRL_TIMBER: {
      const timber = mtrl.getTimberData();
      const solid = timber?.getSolidData();
      if (solid) return solid.meanDensity || solid.density || 0;
      return timber?.getBoardData()?.density || 0;
    }
    case ng.material.Mtrl.MTRL_REINFORCEMENT:
      return mtrl.getReinforcementData()?.properties?.density || DEFAULT_STEEL_DENSITY;
    default:
      return 0;
  }
}

const findMaterial = (guid: string | undefined, input: ElementDbs): ng.material.Data | null => {
  const raw = guid ? (input.mtrlDb ?? []).find((m) => m.id?.guid === guid) : null;
  if (!raw) return null;
  // fromObject refuses an object that is already an instance (it wants JSON), so pass those through.
  return raw instanceof ng.material.Data ? raw : ng.material.Data.fromObject(raw);
};

const findSection = (guid: string | undefined, input: ElementDbs): ng.sections.Section | null => {
  const raw = guid ? (input.secDb ?? []).find((s) => s.id?.guid === guid) : null;
  if (!raw) return null;
  return raw instanceof ng.sections.Section ? raw : ng.sections.Section.fromObject(raw);
};

// Shoelace area of a closed polyline [m²].
const polylineArea = (poly: ng.geometry.PolyLine2D | null | undefined): number => {
  const pts = poly?.points ?? [];
  if (pts.length < 3) return 0;
  let twice = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    twice += p.x * q.y - q.x * p.y;
  }
  return Math.abs(twice) / 2;
};

// Cross-section area [m²]: `units.a`, or the perimeter less its holes when that is absent.
const sectionArea = (sec: ng.sections.Section | null): number => {
  if (!sec) return 0;
  if ((sec.units?.a ?? 0) > 0) return sec.units!.a;
  const area =
    polylineArea(sec.polylines?.perimeter) -
    (sec.polylines?.holes ?? []).reduce((sum, hole) => sum + polylineArea(hole), 0);
  return Math.max(area, 0);
};

// Outer width (sectional y) and height (sectional z) [m] from the perimeter polyline.
const sectionOuterSize = (sec: ng.sections.Section | null): { width: number; height: number } => {
  const pts = sec?.polylines?.perimeter?.points ?? [];
  if (pts.length < 3) return { width: 0, height: 0 };
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  return { width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
};

// Concrete covers [m], per side. `CoverAndSpace.cover` is in mm.
const elementCovers = (concrete: ng.beam.ConcreteElement) => {
  const covers = { top: 0, btm: 0, lft: 0, rgt: 0 };
  for (const dist of concrete.distances ?? []) {
    const cover = (dist.cover ?? 0) * 1.0e-3;
    switch (dist.side) {
      case ng.design.concrete.BeamSide.BEAM_SIDE_TOP:
        covers.top = cover;
        break;
      case ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM:
        covers.btm = cover;
        break;
      case ng.design.concrete.BeamSide.BEAM_SIDE_LEFT:
        covers.lft = cover;
        break;
      case ng.design.concrete.BeamSide.BEAM_SIDE_RIGHT:
        covers.rgt = cover;
        break;
    }
  }
  return covers;
};

// Centre-line length of one link [m] — mirrors LinkCentreLineLength in WS_PRE_Stress. Note that
// `ng.link.Group.diameter` is in metres, unlike bar diameters, which are in millimetres.
const linkCentreLineLength = (
  grp: ng.link.Group,
  width: number,
  height: number,
  covers: ReturnType<typeof elementCovers>,
): number => {
  const dia = grp.diameter;
  const legHor = width - covers.lft - covers.rgt - dia;
  let legVert = height - covers.top - covers.btm - dia;
  if (legHor <= 0 || legVert <= 0) return 0;

  const angle = grp.angle > 0 ? grp.angle : 90;
  const sinAngle = Math.abs(Math.sin((angle * Math.PI) / 180));
  if (sinAngle > 1.0e-6) legVert /= sinAngle;

  const legs = grp.legs > 0 ? grp.legs : 2;
  const stirrupsPerLink = Math.max(1, legs / 2);
  const hooks = 2 * Math.max(10 * dia, 0.07);
  return stirrupsPerLink * (2 * (legHor + legVert) + hooks);
};

/** A contribution to the line mass [kg/m], constant over [start, end) along the element. */
interface Band {
  start: number;
  end: number;
  massPerLength: number;
}

// Steel bands of a concrete element: the steel's own mass less the concrete it displaces, so that
// adding them to the gross concrete term leaves concrete net of steel.
const reinforcementBands = (
  input: ElementDbs,
  beam: ng.beam.Data,
  concreteDensity: number,
  elementLength: number,
): Band[] => {
  const concrete = beam.getConcrete();
  if (!concrete) return [];
  const bands: Band[] = [];

  // Longitudinal bars, from their exact end points. A bar with no extent along the element (start
  // and end both at x = 0, as projects stored before sx-column normalised them carry) is a position
  // in the section only; it runs the full element, which is what normalizeStandaloneBars makes of it.
  for (const bar of concrete.rebars?.standaloneBars ?? []) {
    const a = bar.startPoint;
    const b = bar.endPoint;
    if (!a || !b) continue;
    const dia = bar.diameter * 1.0e-3; // [mm] -> [m]
    const area = (Math.PI * dia * dia) / 4;
    const steelDensity = elementDensity(findMaterial(bar.mtrlGuid, input)) || DEFAULT_STEEL_DENSITY;
    const axial = Math.abs(b.x - a.x);
    // A sloping bar is longer than the stretch of element it covers.
    const volumePerLength = axial > 1.0e-9 ? (area * Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z)) / axial : area;
    bands.push({
      start: axial > 1.0e-9 ? Math.min(a.x, b.x) : 0,
      end: axial > 1.0e-9 ? Math.max(a.x, b.x) : elementLength,
      massPerLength: volumePerLength * (steelDensity - concreteDensity),
    });
  }

  // Links, smeared over the length of their group. Geometry is referred to the start section of the
  // first segment, as the carbon take-off does.
  const firstSec = findSection(beam.segments[0]?.layer?.start?.secGuid, input);
  const { width, height } = sectionOuterSize(firstSec);
  const covers = elementCovers(concrete);
  for (const grp of concrete.links?.linkGrps ?? []) {
    if (grp.s <= 0 || grp.diameter <= 0 || grp.length <= 0) continue;
    const linkLength = linkCentreLineLength(grp, width, height, covers);
    if (linkLength <= 0) continue;
    const count = Math.floor(grp.length / grp.s) + 1;
    const area = (Math.PI * grp.diameter * grp.diameter) / 4;
    const steelDensity = elementDensity(findMaterial(grp.mtrlGuid, input)) || DEFAULT_STEEL_DENSITY;
    const volume = count * linkLength * area;
    bands.push({
      start: grp.start,
      end: grp.start + grp.length,
      massPerLength: (volume / grp.length) * (steelDensity - concreteDensity),
    });
  }

  return bands;
};

/**
 * The self-weight line load of a beam element [N/m], as trapezoids along its axis from x = 0.
 *
 * Breaks at every segment end and wherever a bar or link group starts or stops, so that each piece
 * is exactly linear: the section area varies linearly within a segment and the steel is constant
 * within a piece. Empty when the element has no material density or no section area.
 */
export function selfWeightLineLoads(input: ElementDbs, beam: ng.beam.Data): SelfWeightPiece[] {
  const density = elementDensity(findMaterial(beam.mtrlGuid, input));
  if (density <= 0) return [];

  // Gross cross-section, per segment, linear between its start and end area.
  const segments: { start: number; end: number; aStart: number; aEnd: number }[] = [];
  let x = 0;
  for (const seg of beam.segments) {
    if (seg.length <= 0) continue;
    const aStart = sectionArea(findSection(seg.layer?.start?.secGuid, input));
    const endSec = findSection(seg.layer?.end?.secGuid, input);
    const aEnd = endSec ? sectionArea(endSec) : aStart;
    segments.push({ start: x, end: x + seg.length, aStart, aEnd });
    x += seg.length;
  }
  const totalLength = x;
  if (totalLength <= 0) return [];

  const bands = beam.getConcrete() ? reinforcementBands(input, beam, density, totalLength) : [];

  const breaks = new Set<number>([0, totalLength]);
  for (const s of segments) breaks.add(s.start).add(s.end);
  for (const b of bands) {
    if (b.start > 0 && b.start < totalLength) breaks.add(b.start);
    if (b.end > 0 && b.end < totalLength) breaks.add(b.end);
  }
  const xs = [...breaks].sort((p, q) => p - q);

  const pieces: SelfWeightPiece[] = [];
  for (let i = 0; i + 1 < xs.length; i++) {
    const x1 = xs[i];
    const x2 = xs[i + 1];
    if (x2 - x1 < 1.0e-9) continue;
    const mid = (x1 + x2) / 2;
    const seg = segments.find((s) => mid >= s.start && mid < s.end);
    if (!seg) continue;
    const areaAt = (px: number) =>
      seg.aStart + ((seg.aEnd - seg.aStart) * (px - seg.start)) / (seg.end - seg.start);
    const steel = bands.reduce((sum, b) => (mid >= b.start && mid < b.end ? sum + b.massPerLength : sum), 0);
    pieces.push({
      start: x1,
      end: x2,
      qStart: (areaAt(x1) * density + steel) * GRAVITY,
      qEnd: (areaAt(x2) * density + steel) * GRAVITY,
    });
  }
  return pieces;
}

/** Mass [kg] carried by a set of self-weight pieces. */
export function massOfSelfWeight(pieces: SelfWeightPiece[]): number {
  return pieces.reduce((sum, p) => sum + ((p.qStart + p.qEnd) / 2) * (p.end - p.start), 0) / GRAVITY;
}

/**
 * The self-weight pieces a project's loading actually holds — the line loads in its
 * `TYPE_SELF_WEIGHT` load case — or null when it has none. What the analysis was given, so a printed
 * weight derived from these cannot disagree with it.
 */
export function storedSelfWeight(loading: ng.loading.Data | null | undefined): SelfWeightPiece[] | null {
  const lcase = loading?.loadcases.find((lc) => lc.type === ng.loadcase.Type.TYPE_SELF_WEIGHT);
  if (!lcase) return null;
  const guid = lcase.id?.guid;
  return (loading?.loads ?? [])
    .filter((l) => l.lcaseGuid === guid && l.positions.length >= 2 && l.values.length >= 2)
    .map((l) => {
      // Measured along the load's own line: projects stored before the element axis was x carry
      // their self-weight along z.
      const [a, b] = l.positions;
      const start = a.x;
      return { start, end: start + Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z), qStart: l.values[0], qEnd: l.values[1] };
    });
}

/**
 * The element's mass [kg] as an app should report it: integrated from the self-weight the project
 * holds, which is what the analysis was given, and calculated only when it holds none. Every place
 * that shows a weight uses this, so none of them can disagree with the analysis or with each other.
 */
export function elementMass(input: ElementDbs, beam: ng.beam.Data, loading: ng.loading.Data | null | undefined): number {
  const stored = storedSelfWeight(loading);
  return massOfSelfWeight(stored?.length ? stored : selfWeightLineLoads(input, beam));
}
